export type CalendarMission = {
  id: string;
  starts_at: string;
  ends_at: string;
  location?: string | null;
  status?: string;
  notes?: string | null;
  client_report?: string | null;
  client_display_name?: string | null;
  client?: { name: string } | null;
};

const compactUtc = (value: string | Date) =>
  new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

const calendarTitle = (mission: CalendarMission) =>
  `ARD — ${mission.client?.name || mission.client_display_name || "Mission"}`;

const icsText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

export function googleCalendarMissionUrl(
  mission: CalendarMission,
  description?: string | null,
) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: calendarTitle(mission),
    dates: `${compactUtc(mission.starts_at)}/${compactUtc(mission.ends_at)}`,
    location: mission.location || "",
    details: description || mission.client_report || mission.notes || "",
    ctz: "Europe/Paris",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function downloadMissionCalendar(
  missions: CalendarMission[],
  filename = "missions-ard.ics",
  calendarName = "ARD Manager",
) {
  const active = missions.filter(
    (mission) => mission.status !== "cancelled" && mission.ends_at > mission.starts_at,
  );
  if (!active.length) return false;

  const stamp = compactUtc(new Date());
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ARD Manager//Planning missions//FR",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${icsText(calendarName)}`,
    ...active.flatMap((mission) => [
      "BEGIN:VEVENT",
      `UID:${icsText(mission.id)}@ard-manager.local`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${compactUtc(mission.starts_at)}`,
      `DTEND:${compactUtc(mission.ends_at)}`,
      `SUMMARY:${icsText(calendarTitle(mission))}`,
      `LOCATION:${icsText(mission.location || "")}`,
      `DESCRIPTION:${icsText(mission.client_report || mission.notes || "")}`,
      "STATUS:CONFIRMED",
      "END:VEVENT",
    ]),
    "END:VCALENDAR",
  ];
  const blob = new Blob([lines.join("\r\n") + "\r\n"], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.replace(/[^a-zA-Z0-9._-]/g, "-");
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
}
